import { createTheme } from '@mui/material';

// HourTrax design tokens — source of truth: docs/ui-refresh-mockup.html
// Light and dark palettes share the same structure; components should read
// from theme.palette (never hardcode hex) so both modes stay in sync.

const light = {
  ground: '#F6F8F8',
  surface: '#FFFFFF',
  surface2: '#FBFCFC',
  border: '#E4E9E9',
  borderStrong: '#D2DAD9',
  text: '#17211F',
  textMuted: '#5C6B68',
  textFaint: '#8A9794',
  accent: '#0D9488',
  accentStrong: '#0B7C72',
  accentSoft: '#E6F4F2',
  onAccent: '#FFFFFF',
  good: '#15803D',
  goodSoft: '#E7F3EB',
  warn: '#B45309',
  warnSoft: '#FBF0DF',
  crit: '#B91C1C',
  critSoft: '#FBE9E9',
  info: '#1D4ED8',
  infoSoft: '#E8EEFC',
  shadow: '0 1px 2px rgba(23,33,31,.04), 0 1px 3px rgba(23,33,31,.06)',
};

const dark = {
  ground: '#0D1413',
  surface: '#141C1B',
  surface2: '#101817',
  border: '#263230',
  borderStrong: '#33403D',
  text: '#E9EEED',
  textMuted: '#9BA9A6',
  textFaint: '#6E7C79',
  accent: '#2DD4BF',
  accentStrong: '#14B8A6',
  accentSoft: '#123230',
  onAccent: '#06201D',
  good: '#4ADE80',
  goodSoft: '#12261A',
  warn: '#FBBF24',
  warnSoft: '#2A2110',
  crit: '#F87171',
  critSoft: '#2A1514',
  info: '#60A5FA',
  infoSoft: '#111E33',
  shadow: '0 1px 2px rgba(0,0,0,.3), 0 1px 3px rgba(0,0,0,.35)',
};

const SANS = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const buildTheme = (mode = 'light') => {
  const t = mode === 'dark' ? dark : light;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: t.accent,
        dark: t.accentStrong,
        light: mode === 'dark' ? '#5EEAD4' : '#14B8A6',
        contrastText: t.onAccent,
        soft: t.accentSoft,
      },
      // One accent: secondary maps to the accent family (legacy color="secondary"
      // usages render teal instead of the old purple).
      secondary: {
        main: t.accentStrong,
        dark: t.accentStrong,
        light: t.accent,
        contrastText: t.onAccent,
        soft: t.accentSoft,
      },
      success: { main: t.good, soft: t.goodSoft },
      warning: { main: t.warn, soft: t.warnSoft },
      error: { main: t.crit, soft: t.critSoft },
      info: { main: t.info, soft: t.infoSoft },
      background: {
        default: t.ground,
        paper: t.surface,
        surface2: t.surface2,
      },
      divider: t.border,
      borderStrong: t.borderStrong,
      text: {
        primary: t.text,
        secondary: t.textMuted,
        disabled: t.textFaint,
        faint: t.textFaint,
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: SANS,
      // Scale from the mockup: xs .6875 / sm .8125 / body .875 / h3 .9375 / h2 1.0625 / display 1.4
      h1: { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.0625rem', fontWeight: 650, letterSpacing: '-0.01em' },
      h3: { fontSize: '0.9375rem', fontWeight: 600 },
      h4: { fontSize: '0.9375rem', fontWeight: 600 },
      h5: { fontSize: '0.9375rem', fontWeight: 600 },
      h6: { fontSize: '1.0625rem', fontWeight: 650, letterSpacing: '-0.01em' },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8125rem' },
      subtitle1: { fontSize: '0.875rem', fontWeight: 600 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
      caption: { fontSize: '0.6875rem' },
      overline: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            // Tabular figures on every number (money, hours, dates) — digits
            // align in columns; proportional text is unaffected.
            fontVariantNumeric: 'tabular-nums',
          },
        },
      },
      // Kill MUI's dark-mode elevation gradient and heavy shadows: flat surfaces,
      // hairline borders, barely-there shadow.
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${t.border}`,
            boxShadow: t.shadow,
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: `1px solid ${mode === 'dark' ? t.border : 'transparent'}`,
            ...(mode === 'dark' && {
              backgroundColor: t.surface,
              color: t.text,
            }),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            '@media (max-width: 600px)': { minHeight: 44 },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': { padding: 12 },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            '@media (max-width: 600px)': {
              minHeight: 48,
              minWidth: 'auto',
              padding: '6px 12px',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: t.surface2,
            color: t.textFaint,
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap',
            borderBottom: `1px solid ${t.border}`,
          },
          root: {
            fontSize: '0.8125rem',
            borderBottom: `1px solid ${t.border}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: t.surface2 },
            '&:last-child td': { borderBottom: 'none' },
          },
          head: {
            '&:hover': { backgroundColor: 'transparent' },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${t.border}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${t.border}`,
            boxShadow: t.shadow,
            backgroundImage: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: '0.6875rem' },
        },
      },
    },
  });
};

export default buildTheme;